import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import interventionService from '../../../services/interventionService';
import clientService from '../../../services/clientService';
import localService from '../../../services/localService';
import InterventionWarningModal from './InterventionWarningModal';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';
const InterventionEditModal = ({ isOpen, interventionId, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [form, setForm] = useState({
    id: '',
    startDate: '',
    endDate: '',
    clientId: '',
    localId: '',
    typeIntervention: 1,
    observation: '',
    cloture: false
  });
  const [clients, setClients] = useState([]);
  const [locals, setLocals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningData, setWarningData] = useState([]);
  const [wasCloture, setWasCloture] = useState(false);
  const [pendingSave, setPendingSave] = useState(false); // Track if save is pending after warning
  const [intervention, setIntervention] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    clientService.getClients(1000, 1).then(res => {
      if (res.success && Array.isArray(res.data.list)) setClients(res.data.list);
    });
  }, [isOpen]);

  // Fetch locals when modal opens
  useEffect(() => {
    if (!isOpen) {
      setLocals([]);
      return;
    }
    localService.getLocaux(1000, 1).then(res => {
      if (res.success && Array.isArray(res.data.list)) {
        setLocals(res.data.list);
      } else {
        setLocals([]);
      }
    });
  }, [isOpen]);

  // Fetch intervention details when interventionId changes
  useEffect(() => {
    if (isOpen && interventionId) {
      setLoading(true);
      interventionService.getInterventionById(interventionId).then(res => {
        if (res.success) setIntervention(res.data);
        setLoading(false);
      });
    }
  }, [isOpen, interventionId]);

  // Set form state when intervention and locals are ready
  useEffect(() => {
    if (intervention && locals.length > 0) {
      setForm({
        id: intervention.id,
        startDate: intervention.startDate ? intervention.startDate.slice(0, 16) : '',
        endDate: intervention.endDate ? intervention.endDate.slice(0, 16) : '',
        clientId: intervention.clientId || '',
        localId: intervention.localId || '',
        typeIntervention: intervention.typeIntervention || 1,
        observation: intervention.observation || '',
        cloture: intervention.cloture || false
      });
      setWasCloture(!!intervention.cloture);
    }
  }, [intervention, locals]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    const switchedToCloture = !wasCloture && form.cloture;
    if (switchedToCloture && !showWarningModal) {
      // Show warning modal first, do not save yet
      setLoading(true);
      try {
        const warnRes = await interventionService.getWarningHistory(form.id);
        setWarningData(warnRes.success ? warnRes.data : []);
        setShowWarningModal(true);
        setPendingSave(true); // Mark that save is pending after warning
      } catch {
        setWarningData([]);
        setShowWarningModal(true);
        setPendingSave(true);
      } finally {
        setLoading(false);
      }
      return;
    }
    // If not switching to cloture, or already confirmed warning, proceed to save
    setLoading(true);
    try {
      const payload = {
        ...form,
        clientId: Number(form.clientId),
        localId: form.localId ? Number(form.localId) : null,
      };
      const res = await interventionService.updateIntervention(payload);
      if (res.success) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setError(res.message);
      }
    } catch {
      setError('Erreur lors de la modification');
    } finally {
      setLoading(false);
      setPendingSave(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen || !intervention) return null;

  return (
    <>
      {showWarningModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <InterventionWarningModal
            isOpen={showWarningModal}
            warnings={warningData}
            onClose={() => {
              setShowWarningModal(false);
              if (pendingSave) {
                // User confirmed, now actually save
                setPendingSave(false);
                handleSubmit({ preventDefault: () => {} });
              }
            }}
          />
        </div>
      )}
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 ${showWarningModal ? 'pointer-events-none select-none' : ''}`}>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
          {/* Header */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'} px-6 py-4 flex-shrink-0`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`${isDarkMode ? 'bg-white bg-opacity-10' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-xl flex items-center justify-center mr-3`}>
                  <i className="fas fa-edit text-white text-lg"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Modifier intervention</h2>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>
                    Intervention #{intervention.id}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                disabled={showWarningModal}
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            <div className="p-6">
              {error && (
                <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  <div className="flex items-center">
                    <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                    <div>
                      <div className="font-medium">Erreur</div>
                      <div className="text-sm">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-xl p-6`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Date début *</label>
                      <input type="datetime-local" name="startDate" value={form.startDate} onChange={handleChange} required className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Date fin *</label>
                      <input type="datetime-local" name="endDate" value={form.endDate} onChange={handleChange} required className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Client *</label>
                      <ClientSelectDropdown
                        value={form.clientId}
                        onChange={val => setForm(f => ({ ...f, clientId: val }))}
                        options={clients}
                        loading={false}
                        disabled={false}
                        isDarkMode={isDarkMode}
                        required={true}
                        placeholder="Sélectionner un client"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Local *</label>
                      <LocalSelectDropdown
                        value={form.localId}
                        onChange={val => setForm(f => ({ ...f, localId: val }))}
                        options={locals}
                        loading={false}
                        disabled={false}
                        isDarkMode={isDarkMode}
                        required={true}
                        placeholder="Sélectionner un local"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Type *</label>
                      <select name="typeIntervention" value={form.typeIntervention} onChange={handleChange} required className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'}`}>
                        <option value={1}>Préventive</option>
                        <option value={2}>Curative</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Observation</label>
                      <textarea
                        name="observation"
                        value={form.observation}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                      ></textarea>
                    </div>
                    <div className="flex items-center mt-2">
                      <input type="checkbox" name="cloture" checked={form.cloture} onChange={handleChange} required className="mr-2" />
                      <label>Clôturée *</label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className={`border-t px-6 py-4 flex-shrink-0 flex justify-end gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg transition-colors font-medium border ${isDarkMode ? 'text-gray-200 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'}`}
              disabled={showWarningModal}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                loading ||
                !form.startDate ||
                !form.endDate ||
                !form.clientId ||
                !form.localId ||
                !form.typeIntervention ||
                showWarningModal // <-- removed !form.observation here
              }
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading || !form.startDate || !form.endDate || !form.clientId || !form.localId || !form.typeIntervention || showWarningModal
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-primary-700 hover:bg-primary-800 text-white shadow-lg hover:shadow-xl'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sauvegarde...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Sauvegarder
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InterventionEditModal;